import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { TrendEvent } from "../../endpoints/results.type";
import type {
  CreateTrendEventBody,
  UpdateTrendEventBody,
} from "../../endpoints/trends.endpoints";
import {
  useCreateTrendEventMutation,
  useUpdateTrendEventMutation,
  useDeleteTrendEventMutation,
} from "../../endpoints/trends.endpoints";
import { Pencil, Trash2, Plus } from "lucide-react";

interface ManageTrendEventsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surveyId: string;
  events: TrendEvent[];
}

/** Format ISO datetime for datetime-local input (YYYY-MM-DDTHH:mm). */
function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocal(value: string): string {
  if (!value) return "";
  return new Date(value).toISOString();
}

const emptyForm: CreateTrendEventBody = {
  short_title: "",
  long_title: "",
  description: "",
  image_url: "",
  event_date: "",
  links: [],
  display_order: 0,
};

const MAX_LINKS = 3;

const ManageTrendEventsModal: React.FC<ManageTrendEventsModalProps> = ({
  open,
  onOpenChange,
  surveyId,
  events,
}) => {
  const [mode, setMode] = useState<"list" | "add" | "edit">("list");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateTrendEventBody>(emptyForm);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [createEvent, { isLoading: isCreating }] = useCreateTrendEventMutation();
  const [updateEvent, { isLoading: isUpdating }] = useUpdateTrendEventMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteTrendEventMutation();

  const isSaving = isCreating || isUpdating;

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setMode("list");
  };

  const openAdd = () => {
    setForm({
      ...emptyForm,
      event_date: toDatetimeLocal(new Date().toISOString()),
      display_order: events.length,
    });
    setMode("add");
  };

  const openEdit = (event: TrendEvent) => {
    setEditingId(event.id);
    setForm({
      short_title: event.short_title,
      long_title: event.long_title,
      description: event.description ?? "",
      image_url: event.image_url ?? "",
      event_date: event.event_date ? toDatetimeLocal(event.event_date) : "",
      links: event.links?.length ? [...event.links] : [],
      display_order: event.display_order ?? 0,
    });
    setMode("edit");
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      setDeleteConfirmId(null);
    }
  }, [open]);

  const handleSave = async() => {
    if (!form.short_title.trim() || !form.long_title.trim() || !form.event_date) return;
    const links = (form.links ?? []).filter(Boolean);
    const eventDateIso = form.event_date
      ? new Date(form.event_date).toISOString()
      : "";
    const payload = {
      ...form,
      // Backend expects both the FK (`survey`) and the identifier (`survey_id`)
      // when creating/updating trend events.
      survey: surveyId,
      short_title: form.short_title.trim(),
      long_title: form.long_title.trim(),
      description: form.description?.trim() || null,
      image_url: form.image_url?.trim() || null,
      event_date: eventDateIso,
      links: links.length ? links : null,
      display_order: form.display_order ?? 0,
    };
    try {
      if (mode === "add") {
        await createEvent({ ext_survey_id: surveyId, body: payload }).unwrap();
      } else if (editingId) {
        const updateBody: UpdateTrendEventBody = { ...payload };
        await updateEvent({
          ext_survey_id: surveyId,
          event_id: editingId,
          body: updateBody,
        }).unwrap();
      }
      resetForm();
    } catch {
      // Error handled by API layer / notification
    }
  };

  const handleDelete = async(eventId: string) => {
    try {
      await deleteEvent({ ext_survey_id: surveyId, event_id: eventId }).unwrap();
      setDeleteConfirmId(null);
    } catch {
      // Error handled by API layer
    }
  };

  const setLink = (index: number, value: string) => {
    const links = [...(form.links ?? [])];
    while (links.length <= index) links.push("");
    links[index] = value;
    setForm({ ...form, links });
  };

  const sortedEvents = [...events].sort(
    (a, b) =>
      new Date(a.event_date).getTime() - new Date(b.event_date).getTime()
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto bg-white"
        // Prevent closing on outside click/overlay interaction.
        onPointerDownOutside={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onInteractOutside={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        // Prevent closing on Escape key.
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "list" && "Manage points of interest"}
            {mode === "add" && "Add event"}
            {mode === "edit" && "Edit event"}
          </DialogTitle>
        </DialogHeader>

        {mode === "list" && (
          <>
            <div className="space-y-2">
              {sortedEvents.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">
                  No events yet. Add one to show on the trend graphs.
                </p>
              ) : (
                <ul className="space-y-1">
                  {sortedEvents.map((event) => (
                    <li
                      key={event.id}
                      className="flex items-center justify-between gap-2 rounded border border-gray-200 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {event.short_title}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(event.event_date).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            }
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="smallIcon"
                          onClick={() => openEdit(event)}
                          aria-label="Edit event"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {deleteConfirmId === event.id ? (
                          <span className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="delete"
                              size="sm"
                              onClick={() => handleDelete(event.id)}
                              disabled={isDeleting}
                            >
                              Confirm
                            </Button>
                            <Button
                              type="button"
                              variant="gray"
                              size="sm"
                              onClick={() => setDeleteConfirmId(null)}
                            >
                              Cancel
                            </Button>
                          </span>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="smallIcon"
                            onClick={() => setDeleteConfirmId(event.id)}
                            aria-label="Delete event"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="create"
                size="med"
                onClick={openAdd}
              >
                <Plus className="w-4 h-4" />
                Add event
              </Button>
            </DialogFooter>
          </>
        )}

        {(mode === "add" || mode === "edit") && (
          <>
            <div className="grid gap-4 py-2">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Short title (on graph)
                </label>
                <Input
                  value={form.short_title}
                  onChange={(e) =>
                    setForm({ ...form, short_title: e.target.value.slice(0, 80) })
                  }
                  placeholder="e.g. Product launch"
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Long title (in popup)
                </label>
                <Input
                  value={form.long_title}
                  onChange={(e) =>
                    setForm({ ...form, long_title: e.target.value.slice(0, 255) })
                  }
                  placeholder="e.g. Competitor launches new product line"
                  maxLength={255}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Event date
                </label>
                <Input
                  type="datetime-local"
                  value={
                    form.event_date
                      ? form.event_date.slice(0, 16)
                      : ""
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      event_date: fromDatetimeLocal(e.target.value),
                    })
                  }
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Description (optional)
                </label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.description ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="A key competitor released..."
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Image URL (optional)
                </label>
                <Input
                  type="url"
                  value={form.image_url ?? ""}
                  onChange={(e) =>
                    setForm({ ...form, image_url: e.target.value })
                  }
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Links (optional, 1–3)
                </label>
                {Array.from({ length: MAX_LINKS }, (_, i) => (
                  <Input
                    key={i}
                    type="url"
                    className="mt-1"
                    value={form.links?.[i] ?? ""}
                    onChange={(e) => setLink(i, e.target.value)}
                    placeholder={`Link ${i + 1}`}
                  />
                ))}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">
                  Display order
                </label>
                <Input
                  type="number"
                  value={form.display_order ?? 0}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      display_order: parseInt(e.target.value, 10) || 0,
                    })
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="create"
                size="med"
                onClick={handleSave}
                disabled={
                  isSaving ||
                  !form.short_title.trim() ||
                  !form.long_title.trim() ||
                  !form.event_date
                }
              >
                {mode === "add" ? "Add event" : "Save changes"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ManageTrendEventsModal;
